<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'

import AppSidebar from '../shell/components/AppSidebar.vue'
import AppSplash from '../shell/components/AppSplash.vue'
import AppTopbar from '../shell/components/AppTopbar.vue'
import BootErrorState from '../shell/components/BootErrorState.vue'
import WorkspaceEmptyState from '../shell/components/WorkspaceEmptyState.vue'
import { useBootstrap } from '../shell/useBootstrap'

import { useNavStore } from '@/app/navigation'
import { useWorkspace } from '@/features/account'
import {
  AccountPaymentBanner,
  FreePlanRouteGate,
  PlanSelectionSurface,
  TrialBanner,
  useFreePlanRouteLock,
  usePlanSelection,
} from '@/features/credits'
import { AddToDashboardModal } from '@/features/dashboards'
import { ConnectionLostBanner, MetaCheckpointBanner } from '@/features/global-surface'
import { useI18n } from '@/shared/i18n'
import { useToast } from '@/shared/ui'
import { Alert } from '@/shared/ui/alert'
import { Button } from '@/shared/ui/button'

const { t } = useI18n()
// The composition root installs the one-flight coordinator before the first navigation. The
// layout consumes its render state only; it never starts a second account boot.
const bootstrap = useBootstrap()
const navStore = useNavStore()
const route = useRoute()
const { workspaceId, workspaces } = useWorkspace()
const planSelection = usePlanSelection()
const freePlanLock = useFreePlanRouteLock()
const toast = useToast()
const retrying = ref(false)
const retryingSections = ref(false)

const routeKey = computed(() => String(workspaceId.value ?? 'no-workspace'))
// Ledger §C defect 3: a booted account with zero workspaces gets a real empty state, not a
// blank `<main>` around a workspace-scoped route that has nothing to render.
const hasNoWorkspace = computed(() => workspaces.value.length === 0)
// A section the server marked `retryable: false` is a deterministic failure — re-asking returns the
// same answer, so offering the button would be a lie. Show it only when something can actually move.
const hasRetryableSections = computed(() =>
  bootstrap.degradedSections.value.some((section) => section.retryable),
)
const contentInsetUtilities = {
  compact: '[--ui-page-current-inset:var(--ui-page-inset-compact)]',
  tight: '[--ui-page-current-inset:var(--ui-page-inset-tight)]',
  none: '[--ui-page-current-inset:0]',
  spacious: '[--ui-page-current-inset:var(--ui-page-inset-spacious)]',
} as const

// A Free-locked route renders `FreePlanRouteGate`, not its page — so a full-bleed page's chrome
// opt-outs (Orpheus: no sidebar, no inset) must not apply to the locked screen (ORP-26799).
const contentInsetClass = computed(() => {
  const routeInset = freePlanLock.value ? 'compact' : (route.meta.contentInset ?? 'compact')
  const inset = (
    planSelection.requested.value ? 'none' : routeInset
  ) as keyof typeof contentInsetUtilities
  return [`app-layout__content--${inset}`, contentInsetUtilities[inset]]
})
const hidesAppSidebar = computed(
  () =>
    planSelection.requested.value ||
    (freePlanLock.value === null && route.meta.appSidebar === 'hidden'),
)
// One mutually-exclusive utility, never several `grid-cols-*`/`grid-cols-[…]` classes
// at once: Tailwind emits the named `grid-cols-[…]` form and the raw arbitrary-property form in
// different stylesheet buckets, so which one wins a same-specificity tie is an implementation
// detail, not something either class's position in `:class` controls. Stacking them (as the
// settings override used to) let the always-present 5rem default silently beat the 18rem
// override, so the sidebar rendered wider than its grid track and overlaid the page content.
const frameGridClass = computed(() => {
  if (hidesAppSidebar.value) return 'grid-cols-[minmax(0,1fr)]'
  if (navStore.isSettingsOpen && !planSelection.requested.value) {
    return 'grid-cols-[18rem_minmax(0,1fr)]'
  }
  return 'grid-cols-[5rem_minmax(0,1fr)]'
})

async function retry(): Promise<void> {
  retrying.value = true
  try {
    await bootstrap.retry()
  } finally {
    retrying.value = false
  }
}

/**
 * ORP-20409: re-ask only for the sections the boot could not resolve. Distinct from `retry()`
 * above — that one re-boots behind the splash. Here `status` stays `ready`, so the banner is the
 * only thing that changes and the page the user is reading is never blanked.
 *
 * The flow is headless and returns its outcome; the copy belongs here (contract 07 §13). A
 * `stale` result is silent on purpose: it means the user switched context mid-request, so the
 * banner they were acting on no longer describes anything.
 */
async function retrySections(): Promise<void> {
  retryingSections.value = true
  try {
    const outcome = await bootstrap.retryDegradedSections()
    // Static keys only: `no-dynamic-keys` is whitelisted for feature screens (contract 04 §13.4)
    // and deliberately NOT for the shell, so the classified `outcome.error.i18nKey` is not rendered
    // here. The banner stays up either way — the sections are still degraded — so the toast only
    // has to say that the re-ask did not land.
    if (outcome.status === 'failed') {
      toast.error(t('shell.boot.degradedRetryFailed'))
    } else if (outcome.status === 'retried' && outcome.skipped.length > 0) {
      toast.warning(t('shell.boot.degradedPartial', { count: outcome.skipped.length }))
    }
  } finally {
    retryingSections.value = false
  }
}
</script>

<template>
  <AppSplash
    v-if="
      bootstrap.status.value === 'idle' ||
      bootstrap.status.value === 'booting' ||
      (bootstrap.status.value === 'ready' && !bootstrap.isRouteReady.value)
    "
  />
  <BootErrorState
    v-else-if="bootstrap.status.value === 'error'"
    :retrying="retrying"
    @retry="retry"
  />
  <div v-else class="app-layout min-h-dvh overflow-hidden bg-canvas">
    <AppTopbar />
    <TrialBanner />
    <AccountPaymentBanner />
    <MetaCheckpointBanner />
    <ConnectionLostBanner />
    <div
      class="app-layout__frame grid max-[56rem]:grid-cols-[minmax(0,1fr)]"
      :class="[
        frameGridClass,
        {
          'app-layout__frame--settings': navStore.isSettingsOpen && !planSelection.requested.value,
          'app-layout__frame--sidebar-hidden': hidesAppSidebar,
        },
      ]"
    >
      <AppSidebar v-if="!hidesAppSidebar" />
      <main
        class="app-layout__content h-[calc(100dvh-4.5rem)] min-w-0 p-[var(--ui-page-current-inset)]"
        :class="contentInsetClass"
      >
        <Alert v-if="bootstrap.isDegraded.value" class="app-layout__degraded mb-md" tone="warning">
          {{
            t('shell.boot.degraded', {
              count: bootstrap.degradedSections.value.length,
            })
          }}
          <Button
            v-if="hasRetryableSections"
            class="app-layout__degraded-retry"
            variant="outline"
            size="sm"
            :loading="retryingSections"
            @click="retrySections"
          >
            {{ t('shell.boot.degradedRetry') }}
          </Button>
        </Alert>
        <PlanSelectionSurface v-if="planSelection.requested.value" />
        <WorkspaceEmptyState v-else-if="hasNoWorkspace" />
        <div v-else :key="routeKey" class="app-layout__route h-full min-w-0 overflow-y-auto">
          <FreePlanRouteGate>
            <slot />
          </FreePlanRouteGate>
        </div>
      </main>
    </div>
    <AddToDashboardModal />
  </div>
</template>
